import axios from 'axios';
import { clearToken, saveToken, setupToken } from '../helpers/authTokenHelpers';
import {
  getAPIErrorReason,
  getCookie,
  isElectronApp,
  isEmail,
  isEmpty,
  isIOS,
  isIpad,
  isReactNative
} from '../helpers/common';
import { appInit } from './appService';
import jwt_decode from 'jwt-decode';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { ResetPasswordDetailInterface } from '../interfaces/SettingsInterface';
import { COLOR_THEME, COLOR_THEME_TYPE , STORAGE_KEYS } from '../global/constants';
import { clearAnalyticsUser, trackAnalyticActivity } from './analyticsService';
import { captureException } from './logService';
import { CustomWindow } from '../interfaces/OneSignalInterface';
import LocalDbService from './localDbService';
import {
  ForgotPayloadInterface,
  LoginPayloadInterface,
  PasswordDetailInterface,
  SignUpPayloadInterface
} from '../interfaces/AuthInterface';
import { ACCOUNT_ANALYTICS } from '../global/analyticsConstants';
import { nanoid } from 'nanoid';
import { detect } from 'detect-browser';
import { clearPreviousWorkspaceData } from './baseServices';
import MobxTasksCacheService from './mobxTasksCacheService';
import DraftMessageCacheService from './draftMessageCacheService';
import FilesCacheService from './filesCacheService';
import MessageGroupCacheService from './messageGroupCacheService';
import ConversationMessageCacheService from './conversationMessageCacheService';
import { cancelRowRefresh } from './chatMessageSyncService';
import MyTimeLogCacheService from './myTimeLogCacheService';
import TimeTrackingCacheService from './timeTrackingCacheService';
import { rootStore } from '../mobx/rootStore';
import MobxProjectsCacheService from './mobxProjectsCacheService';
import { LoginResponseItem } from '../mobx/interfaces/user';
import { cleanupAllFirebaseListenersAndSignOut } from '../helpers/firebaseHelper';

declare const window: CustomWindow;

interface JwtPayload {
  exp: number;
}

type DeviceInfo = {
  deviceId: string;
  deviceType: string;
};

type GoogleLoginPayload = {
  idToken: string;
  deviceInfo: DeviceInfo;
  appName: string;
  referCode?: string; // Optional property
};

//get device info
const getDeviceInfo = () => {
  let storedDeviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  if (!storedDeviceId) {
    storedDeviceId = nanoid();
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, storedDeviceId);
  }
  if (isReactNative()) {
    return {
      deviceId: storedDeviceId,
      deviceType: `${isIpad() ? 'iPad' : 'Mobile'} on ${isIOS() ? 'iOS' : 'Android'}`
    };
  }
  const browser = detect();
  if (isElectronApp()) {
    return {
      deviceId: storedDeviceId,
      deviceType: `Teamcamp Desktop on ${browser?.os}`
    };
  }
  const deviceType =
    browser?.name && browser?.os
      ? `${browser.name.charAt(0).toUpperCase() + browser.name?.slice(1)} on ${browser.os}`
      : '';
  return {
    deviceId: storedDeviceId,
    deviceType
  };
};

/**
 * @desc Login - Get User Token
 * @param {*} obj Data Obj
 */
export const login = async (obj: LoginPayloadInterface) => {
  try {
    if (!obj || !obj.username) {
      dispatchAuthError('Username is Required');
      return;
    } else if (!obj.password) {
      dispatchAuthError('Password is Required');
      return;
    }
    const payload = {
      ...(obj || {}),
      deviceInfo: getDeviceInfo()
    };
    rootStore.appStore.setAuthLoading(true);
    // Ensure database is properly initialized before login
    try {
      await LocalDbService.openDatabase();
    } catch (error) {
      captureException(error);
    }
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/signin`, payload);
    const { data } = response;
    if (data?.error) {
      dispatchAuthError(data?.error);
      return false;
    }
    if (data) {
      setLoginDetail(data);
      const result = await appInit();
      trackAnalyticActivity(ACCOUNT_ANALYTICS.SIGNED_IN);
      const userInfo = {
        id: data?.id,
        email: data?.email,
        name: data?.given_name
      };
      type PostHogIdentify = {
        identify: (id: string, properties?: Record<string, unknown>) => void;
      };
      const posthog = (window as unknown as { posthog?: PostHogIdentify }).posthog;
      if (posthog) {
        posthog.identify(userInfo.id, { email: userInfo.email, name: userInfo.name });
      }
      dispatchAuthSucess('Login successfull');
      return result;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to login please try again';
    captureException(error);
    dispatchAuthError(error);
    return false;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

export const googleLogin = async (idToken: string) => {
  try {
    if (!idToken) {
      dispatchAuthError('Google ID Token is required');
      return;
    }

    const payload: GoogleLoginPayload = {
      idToken,
      deviceInfo: getDeviceInfo(),
      appName: 'WebAppProject'
    };

    const referCode = getCookie('cross_domain_referral');
    if (!isEmpty(referCode)) {
      payload.referCode = referCode;
    }

    rootStore.appStore.setAuthLoading(true);

    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/googleAuth`, payload);
    const { data } = response;
    if (data?.error) {
      dispatchAuthError(data?.error);
      return false;
    }
    if (data) {
      setLoginDetail(data);
      const result = await appInit();

      if (data?.isSignup) trackAnalyticActivity(ACCOUNT_ANALYTICS.CREATED);
      else trackAnalyticActivity(ACCOUNT_ANALYTICS.SIGNED_IN);
      const userInfo = {
        id: data?.id,
        email: data?.email,
        name: data?.given_name
      };
      type PostHogIdentify = {
        identify: (id: string, properties?: Record<string, unknown>) => void;
      };
      const posthog = (window as unknown as { posthog?: PostHogIdentify }).posthog;
      if (posthog) {
        posthog.identify(userInfo.id, { email: userInfo.email, name: userInfo.name });
      }
      dispatchAuthSucess('Login successfull');
      return result;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to login with Google, please try again';
    captureException(error);
    dispatchAuthError(error);
    return false;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

/**
 * @desc set login token and set user
 */
export const setLoginDetail = async (item: LoginResponseItem) => {
  const domain = '.teamcamp.app';
  const decoded = jwt_decode<JwtPayload>(item?.access_token);
  const expirationDate = new Date(decoded?.exp * 1000);
  document.cookie = `cross-domain-email=${
    item?.email
  }; expires=${expirationDate.toUTCString()}; path=/; domain=${domain}`;
  document.cookie = `cross-domain-name=${
    item?.given_name
  }; expires=${expirationDate.toUTCString()}; path=/; domain=${domain}`;
  document.cookie = `login=true; expires=${expirationDate.toUTCString()}; path=/; domain=${domain}`;
  document.cookie = `logged-in-user=${item?.id}; expires=${expirationDate.toUTCString()}; path=/; domain=${domain}`;

  saveToken(item.access_token);
  await UserPreferenceSingleton.getInstance().setCurrentUser(item);
  UserPreferenceSingleton.getInstance().setThemeMode(COLOR_THEME.LIGHT);
  rootStore.appStore.setThemeMode(COLOR_THEME_TYPE.LIGHT);
  rootStore.appStore.setThemeType(COLOR_THEME.LIGHT);
};

/**
 * @desc Log user out
 */
export const logout = async (navigateToLogin?: boolean) => {
  try {
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

    // Tear down every active Firebase listener (workspace data + auth) and sign out of
    // Firebase auth so no listener, token refresh, or session outlives the logout.
    await cleanupAllFirebaseListenersAndSignOut();

    if (window.electronApi?.unsubscribeFcm) {
      window.electronApi.unsubscribeFcm();
    }
    if (isReactNative()) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'unsubscribe-fcm', data: { accessToken: userDetails?.access_token } })
      );
    }

    await window.OneSignal?.push(function () {
      window.OneSignal?.deleteTags(['userid', 'email']);
    });
    window.OneSignal?.push(function () {
      window.OneSignal?.setSubscription(false);
    });
    const domain = '.teamcamp.app';
    const pastExpirationDate = new Date(0).toUTCString(); // Set expiration time to a past date
    document.cookie = `cross-domain-email=; expires=${pastExpirationDate}; path=/; domain=${domain}`;
    document.cookie = `cross-domain-name=; expires=${pastExpirationDate}; path=/; domain=${domain}`;
    document.cookie = `login=; expires=${pastExpirationDate}; path=/; domain=${domain}`;
    document.cookie = `logged-in-user=; expires=${pastExpirationDate}; path=/; domain=${domain}`;
    trackAnalyticActivity(ACCOUNT_ANALYTICS.SIGNED_OUT);
    clearAnalyticsUser();

    cancelRowRefresh();
    await Promise.all([
      MobxTasksCacheService.removeInstance(),
      DraftMessageCacheService.removeInstance(),
      FilesCacheService.removeInstance(),
      MessageGroupCacheService.removeInstance(),
      ConversationMessageCacheService.removeInstance(),
      MyTimeLogCacheService.removeInstance(),
      TimeTrackingCacheService.removeInstance(),
      MobxProjectsCacheService.removeInstance()
    ]);
    if (navigateToLogin) window.location.href = '/membership/login';
  } catch (error) {
    captureException(error);
  }

  // Delete database after all other cleanup
  try {
    /**
     * Remove token from localStorage
     * Remove auth header for future requests
     * Set current user to {} which will set isAuthenticated to false
     */
    clearToken();
    UserPreferenceSingleton.removeInstance();
    rootStore.appStore.setThemeMode(COLOR_THEME_TYPE.LIGHT);
    rootStore.appStore.setThemeType(COLOR_THEME.LIGHT);
    clearPreviousWorkspaceData();
    await LocalDbService.deleteDatabase();
  } catch (e) {
    captureException(e);
  }

  return true;
};

export const validateIsLoggedIn = () => {
  try {
    const token = setupToken();
    if (token) return true;
    return false;
  } catch (e) {
    captureException(e);
    return false;
  }
};

export const signUp = async (payload: SignUpPayloadInterface) => {
  try {
    if (isEmpty(payload.name)) {
      dispatchAuthError('Please Enter Name');
      return false;
    } else if (isEmpty(payload.email)) {
      dispatchAuthError('Please Enter Email');
      return false;
    } else if (!isEmail(payload.email)) {
      dispatchAuthError('Please Enter valid Email');
      return false;
    } else if (isEmpty(payload.password)) {
      dispatchAuthError('Please Enter Password');
      return false;
    }
    rootStore.appStore.setAuthLoading(true);
    const newPayload = {
      ...(payload || {}),
      deviceInfo: getDeviceInfo()
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/signup`, newPayload);
    const { data } = response;
    if (data?.error) {
      dispatchAuthError(data?.error);
      return false;
    }
    if (data) {
      setLoginDetail(data?.userDetails);
      trackAnalyticActivity(ACCOUNT_ANALYTICS.CREATED);
      const result = await appInit(false);
      dispatchAuthSucess('Signup successfull');
      return result;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to Sign up please try again';
    captureException(error);
    dispatchAuthError(error);
    return false;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

export const forgotPassword = async (payload: ForgotPayloadInterface) => {
  try {
    if (isEmpty(payload.email)) {
      dispatchAuthError('Please Enter Email');
      return false;
    } else if (!isEmail(payload.email)) {
      dispatchAuthError('Please Enter valid Email');
      return false;
    }
    rootStore.appStore.setAuthLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/forgotPassword`, payload);
    const { data } = response;
    if (data?.error) {
      dispatchAuthError(data?.error);
      return false;
    }
    if (data) {
      dispatchAuthSucess(data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to forgot password please try again';
    captureException(error);
    dispatchAuthError(error);
    return false;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

/**
 * @desc change password detail
 */
export const changePasswordDetail = async (payloadData: PasswordDetailInterface) => {
  try {
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const payload = {
      ...payloadData,
      userId: userDetails?.id
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/User/changePassword`, payload);
    const { data } = response;
    if (data?.error) {
      dispatchAuthError(data?.error);
      return;
    } else if (data) {
      dispatchAuthSucess(data?.message);
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to change password please try again';
    captureException(error);
    dispatchAuthError(error);
    return false;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

/**
 * @desc reset password detail
 */
export const resetPasswordDetail = async (payload: ResetPasswordDetailInterface) => {
  try {
    if (isEmpty(payload.email) || payload?.email?.trim() === '') {
      dispatchAuthError('Email is required');
      return;
    } else if (!isEmail(payload.email)) {
      dispatchAuthError('Please enter a valid email address');
      return;
    } else if (isEmpty(payload.password)) {
      dispatchAuthError('Please Enter Password');
      return false;
    } else if (isEmpty(payload.confirmPassword)) {
      dispatchAuthError('Please Enter Confirm Password');
      return false;
    } else if (payload.password !== payload.confirmPassword) {
      dispatchAuthError('Password and Confirm Password must be same');
      return false;
    }
    rootStore.appStore.setAuthLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/resetPassword`, payload);
    const { data } = response;
    if (data?.error) {
      dispatchAuthError(data?.error);
      return false;
    }
    if (data) {
      dispatchAuthSucess(data.message);
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to reset password please try again';
    captureException(error);
    dispatchAuthError(error);
    return false;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

/**
 * @desc Project - Get Invitation Details
 * @param {*}
 */
export const getInvitationDetails = async (invitation_id: string) => {
  try {
    rootStore.appStore.setAuthLoading(true);
    const params = {
      invitationId: invitation_id
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/getInvitation`, { params });
    const { data } = response;
    return data;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get invitation details please try again';
    captureException(error);
    dispatchAuthError(error);
    return false;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

/**
 * @desc Firebase - Get Firebase token
 * @param {*} workspace_id
 */
export const getFirebaseToken = async (workspace_id: string) => {
  try {
    rootStore.appStore.setAuthLoading(true);
    const headers = {
      workspace_id
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/firebase/getFirebaseToken`, { headers });
    const { data } = response;
    if (data?.token) {
      UserPreferenceSingleton.getInstance().setFirebaseCustomToken(workspace_id, data?.token);
      return data?.token;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Firebase token please try again';
    captureException(error);
    dispatchAuthError(error);
    return undefined;
  } finally {
    rootStore.appStore.setAuthLoading(false);
  }
};

function dispatchAuthError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}
function dispatchAuthSucess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
